import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Coord, CoordsData, dimsValueDefault, NamedCoords, PositionsData } from "./PositionsData";
import { Box } from "@mui/material";
import { getScrollbarWidth } from "../Helpers";
import { ContainerSizeInfo } from "../ContainerSizeInfo";
import { ImageInfo } from "../ImageInfo";
import { useMeasureElement } from "../CustomHooks";
import { range, rgbColorAToHex, toFixedNumber } from "../MathHelpers";
import { useComponentEditorContext } from "../ComponentEditorContext";
import { useTreeModifyContext } from "../TreeDataContext";
import { usePathContext } from "../PathInfo";

interface PositionsCanvasGridProps {
  backgroundImage: ImageInfo | undefined;
  nodeState: PositionsData;
  dims: number[];
  zoom: number;
  containerSize: ContainerSizeInfo;
  coords: CoordsData;
  dotsSizeMultiplier: number;
  targetSetName: string;
}

export const PositionsCanvasGrid: React.FC<PositionsCanvasGridProps> = ({
  backgroundImage,
  nodeState,
  dims,
  zoom,
  containerSize,
  coords,
  dotsSizeMultiplier,
  targetSetName,
}) => {
  const { getVisual } = useComponentEditorContext();
  const [foregroundCanvasRef, setForegroundCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const [imageSizeInfo, setImageSizeInfo] = useState({
    width: 0,
    height: 0,
    scaledWidth: 0,
    scaledHeight: 0,
    dx: 0,
    dy: 0,
  });
  const [canvasContainerRef, setCanvasContainerRef] = useState<HTMLDivElement>();
  const containerOverflow = useMeasureElement(canvasContainerRef, zoom);
  const [backgroundElementRef, setBackgroundElementRef] = useState<HTMLElement | null>();
  const [lastRemovedDotIndex, setLastRemovedDotIndex] = useState<number>(-1);
  const [lastRemovedDotName, setLastRemovedDotName] = useState<string>("");

  const { saveChangesAtPath } = useTreeModifyContext();
  const { path } = usePathContext();

  const cellSizeX = useMemo(() => {
    let dimsX = dimsValueDefault;
    if (dims) {
      dimsX = dims[0] || dimsValueDefault;
    }
    return imageSizeInfo.width / dimsX;
  }, [imageSizeInfo.width, dims]);

  const cellSizeY = useMemo(() => {
    let dimsY = dimsValueDefault;
    if (dims) {
      dimsY = dims[1] || dimsValueDefault;
    }
    return imageSizeInfo.height / dimsY;
  }, [imageSizeInfo.height, dims]);

  const measureCanvasContainer = useCallback(
    (node: any) => {
      if (node === null) {
        return;
      }
      setCanvasContainerRef(node);
    },
    [setCanvasContainerRef],
  );

  const measuredForegroundCanvasRef = useCallback(
    (node: any) => {
      if (node === null && node !== foregroundCanvasRef) {
        return;
      }
      setForegroundCanvasRef(node);
    },
    [foregroundCanvasRef],
  );

  const backgroundElement = useMemo(() => {
    if (backgroundImage) {
      return getVisual(
        backgroundImage,
        (newRef) => setBackgroundElementRef(newRef),
        {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        { transform: `scale(${zoom})` },
      );
    }
  }, [backgroundImage, getVisual, zoom]);

  const toggleDot = useCallback(
    (x: number, y: number) => {
      if (!coords) {
        return;
      }
      if (coords instanceof Array) {
        let dotIndex = coords.findIndex((dot) => toFixedNumber(dot.x, 0) === x && toFixedNumber(dot.y, 0) === y);
        let newCoords = coords.slice();
        if (dotIndex >= 0) {
          newCoords.splice(dotIndex, 1);
          setLastRemovedDotIndex(dotIndex);
        } else {
          let targetIndex = lastRemovedDotIndex;
          if (targetIndex === -1) {
            targetIndex = newCoords.length;
          }
          newCoords.splice(targetIndex, 0, { x, y });
          setLastRemovedDotIndex(-1);
        }
        saveChangesAtPath(newCoords, path);
      } else {
        let coordFound = false;
        let coordFoundKey = "";
        Object.keys(coords).forEach((key) => {
          if (toFixedNumber(coords[key].x, 0) === x && toFixedNumber(coords[key].y, 0) === y) {
            coordFound = true;
            coordFoundKey = key;
            return;
          }
        });
        if (coordFound) {
          let newCoords = { ...coords };
          setLastRemovedDotName(coordFoundKey);
          delete newCoords[coordFoundKey];
          saveChangesAtPath(newCoords, path);
        } else {
          let newCoords = Object.assign({}, coords);
          let targetName = lastRemovedDotName;
          if (targetName === "") {
            let newAreaNameIndex = 1;
            while (Object.keys(coords).includes(targetSetName + newAreaNameIndex)) {
              newAreaNameIndex += newAreaNameIndex;
            }
            targetName = targetSetName + newAreaNameIndex;
          }
          newCoords[targetName] = { x, y };
          setLastRemovedDotName("");
          saveChangesAtPath(newCoords, path);
        }
      }
    },
    [coords, path, saveChangesAtPath, targetSetName, lastRemovedDotIndex, lastRemovedDotName],
  );

  const handleCanvasClick = useCallback(
    (event: MouseEvent) => {
      if (!foregroundCanvasRef || !foregroundCanvasRef) return;
      const rect = foregroundCanvasRef?.getBoundingClientRect();

      const scaleX = foregroundCanvasRef.width / rect.width;
      const scaleY = foregroundCanvasRef.height / rect.height;

      const clickX = (event.clientX - rect.left) * scaleX - imageSizeInfo.dx;
      const clickY = (event.clientY - rect.top) * scaleY - imageSizeInfo.dy;

      const gridX = Math.round(clickX / (cellSizeX * zoom));
      const gridY = Math.round(clickY / (cellSizeY * zoom));

      toggleDot(gridX, gridY);
    },
    [foregroundCanvasRef, imageSizeInfo.dx, imageSizeInfo.dy, cellSizeX, zoom, cellSizeY, toggleDot],
  );

  const drawGrid = useCallback(
    (ctxFront: CanvasRenderingContext2D) => {
      if (!imageSizeInfo.dx || !imageSizeInfo.dy) return;
      const canvasWidth = ctxFront.canvas.width;
      const canvasHeight = ctxFront.canvas.height;
      const horizontalStep = cellSizeX * zoom;
      const verticalStep = cellSizeY * zoom;
      const cellOffsetX = imageSizeInfo.dx % horizontalStep;
      const cellOffsetY = imageSizeInfo.dy % verticalStep;
      const minAlfaScaling = 1;
      const maxAlfaScaling = 15;
      const lineAlfaH = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 0, verticalStep));
      const lineAlfaW = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 0, horizontalStep));
      const tenLineAlfaH = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 20, verticalStep));
      const tenLineAlfaW = Math.round(range(maxAlfaScaling, minAlfaScaling, 255, 20, horizontalStep));
      const lineColorH = rgbColorAToHex(0, 0, 0, lineAlfaH);
      const lineColorW = rgbColorAToHex(0, 0, 0, lineAlfaW);
      const tenLineColorH = rgbColorAToHex(255, 0, 0, tenLineAlfaH);
      const tenLineColorW = rgbColorAToHex(255, 0, 0, tenLineAlfaW);

      let lineIndexY = Math.ceil(-imageSizeInfo.dy / verticalStep);
      for (let y = cellOffsetY; y <= canvasHeight; y += verticalStep) {
        ctxFront.strokeStyle = lineColorH;

        if (lineIndexY % 10 === 0) {
          ctxFront.strokeStyle = tenLineColorH;
        }

        ctxFront.beginPath();
        ctxFront.moveTo(0, y);
        ctxFront.lineTo(canvasWidth, y);
        ctxFront.stroke();
        lineIndexY++;
      }

      let lineIndexX = Math.ceil(-imageSizeInfo.dx / horizontalStep);
      for (let x = cellOffsetX; x <= canvasWidth; x += horizontalStep) {
        ctxFront.strokeStyle = lineColorW;
        if (lineIndexX % 10 === 0) {
          ctxFront.strokeStyle = tenLineColorW;
        }

        ctxFront.beginPath();
        ctxFront.moveTo(x, 0);
        ctxFront.lineTo(x, canvasHeight);
        ctxFront.stroke();
        lineIndexX++;
      }
    },
    [cellSizeX, cellSizeY, imageSizeInfo.dy, imageSizeInfo.dx, zoom],
  );

  const drawDots = useCallback(
    (ctxFront: CanvasRenderingContext2D, data: CoordsData) => {
      ctxFront.save();
      let smallestFontSize = Number.MAX_VALUE;
      if (data instanceof Array) {
        data.forEach((dot, index) => {
          renderDotCircles(dot, index);
          renderDotNames(dot, index);
        });
      } else {
        Object.keys(data).forEach((key) => {
          renderDotCircles((data as NamedCoords)[key], key);
          renderDotNames((data as NamedCoords)[key], key);
        });
      }

      function renderDotCircles(dot: Coord, index: number | string) {
        const dotX = dot.x * cellSizeX * zoom + imageSizeInfo.dx;
        const dotY = dot.y * cellSizeY * zoom + imageSizeInfo.dy;
        const smallerCellSize = Math.min(cellSizeX, cellSizeY);
        const maxRadius = smallerCellSize * zoom * dotsSizeMultiplier;

        ctxFront.beginPath();
        ctxFront.lineWidth = 1;
        ctxFront.strokeStyle = "rgba(0, 0, 0, 1)";
        ctxFront.fillStyle = "rgba(0, 255, 255, 0.5)";
        ctxFront.ellipse(dotX, dotY, maxRadius, maxRadius, 0, 0, 2 * Math.PI);
        // Draw a cross on the dot width of 3px
        ctxFront.lineWidth = 3;
        ctxFront.moveTo(dotX - maxRadius, dotY);
        ctxFront.lineTo(dotX + maxRadius, dotY);
        ctxFront.moveTo(dotX, dotY - maxRadius);
        ctxFront.lineTo(dotX, dotY + maxRadius);
        ctxFront.fill();
        ctxFront.stroke();

        // Adjust font size to fit within the circle
        let fontSize = maxRadius * 2; // Start with maxRadius as initial font size
        let textWidth;
        let textAscent;
        let textDescent;
        const textString = index.toString();
        do {
          ctxFront.font = `bold ${fontSize}px Courier New`;
          ctxFront.textAlign = "center";
          ctxFront.textBaseline = "middle";
          const textMetrics = ctxFront.measureText(textString);
          textWidth = textMetrics.width;
          textAscent = textMetrics.fontBoundingBoxAscent;
          textDescent = textMetrics.actualBoundingBoxDescent;
          fontSize -= 1; // Decrease font size until it fits
        } while (textWidth > 2 * maxRadius || textAscent > maxRadius * 0.75 || textDescent > maxRadius * 0.75);
        smallestFontSize = Math.min(smallestFontSize, fontSize);
      }

      function renderDotNames(dot: Coord, index: number | string) {
        // Adjust font size to fit within the circle
        const dotX = dot.x * cellSizeX * zoom + imageSizeInfo.dx;
        const dotY = dot.y * cellSizeY * zoom + imageSizeInfo.dy;
        ctxFront.font = `bold ${smallestFontSize}px Courier New`;
        ctxFront.fillStyle = "rgba(255,255,255,1)";
        ctxFront.textAlign = "center";
        ctxFront.textBaseline = "middle";
        ctxFront.fillText(index.toString(), dotX, dotY);
      }

      ctxFront.restore();
    },
    [cellSizeX, zoom, imageSizeInfo.dx, imageSizeInfo.dy, cellSizeY, dotsSizeMultiplier],
  );

  const drawImage = useCallback(() => {
    let childElement = backgroundElementRef?.children[0] as HTMLElement;
    if (!childElement) return;
    if (!canvasContainerRef) return;
    if (!foregroundCanvasRef) return;

    const width = childElement.offsetWidth;
    const height = childElement.offsetHeight;
    const scaledWidth = width * zoom;
    const scaledHeight = height * zoom;
    let dx: number = (foregroundCanvasRef.width - scaledWidth) / 2;
    let dy: number = (foregroundCanvasRef.height - scaledHeight) / 2;
    setImageSizeInfo({ width, height, scaledWidth, scaledHeight, dx, dy });
  }, [backgroundElementRef?.children, canvasContainerRef, foregroundCanvasRef, zoom]);

  const updateCanvas = useCallback(
    (ctxFront: CanvasRenderingContext2D) => {
      ctxFront.resetTransform();
      // console.log("clearing canvas");
      ctxFront.clearRect(0, 0, ctxFront.canvas.width, ctxFront.canvas.height);
      drawImage();
      drawGrid(ctxFront);
      if (coords) {
        drawDots(ctxFront, coords);
      }
    },
    [drawImage, drawGrid, coords, drawDots],
  );

  useEffect(() => {
    if (foregroundCanvasRef) {
      foregroundCanvasRef.addEventListener("click", handleCanvasClick);
    }

    return () => {
      if (foregroundCanvasRef) {
        foregroundCanvasRef.removeEventListener("click", handleCanvasClick);
      }
    };
  }, [foregroundCanvasRef, handleCanvasClick]);

  useEffect(() => {
    const scrollbarWidth = getScrollbarWidth();
    const ctxFront = foregroundCanvasRef?.getContext("2d");
    if (!foregroundCanvasRef || !ctxFront || !backgroundElementRef) return;

    const containerWidth =
      containerSize.width - 1 - (containerOverflow.getSizeInfo.isOverflowH ? scrollbarWidth + 1 : 0);
    const containerHeight =
      containerSize.height - 1 - (containerOverflow.getSizeInfo.isOverflowW ? scrollbarWidth + 1 : 0);
    let widthNumber = Math.max(imageSizeInfo.scaledWidth + cellSizeX * 2 * zoom, containerWidth);
    let heightNumber = Math.max(imageSizeInfo.scaledHeight + cellSizeY * 2 * zoom, containerHeight);
    foregroundCanvasRef.width = widthNumber;
    backgroundElementRef.style.width = "" + widthNumber + "px";
    foregroundCanvasRef.height = heightNumber;
    backgroundElementRef.style.height = "" + heightNumber + "px";

    updateCanvas(ctxFront);
  }, [
    canvasContainerRef,
    zoom,
    containerSize.width,
    containerSize.height,
    updateCanvas,
    containerOverflow.getSizeInfo.isOverflowH,
    containerOverflow.getSizeInfo.isOverflowW,
    imageSizeInfo.scaledHeight,
    imageSizeInfo.scaledWidth,
    cellSizeX,
    cellSizeY,
    foregroundCanvasRef,
    backgroundElementRef,
  ]);

  useEffect(() => {
    const ctxFront = foregroundCanvasRef?.getContext("2d");
    if (!foregroundCanvasRef || !ctxFront) return;
    updateCanvas(ctxFront);
  }, [foregroundCanvasRef, nodeState, updateCanvas]);

  return (
    <Box className="canvas-container" ref={measureCanvasContainer}>
      {backgroundElement}
      <canvas ref={measuredForegroundCanvasRef} className="canvas-layer" />
    </Box>
  );
};
